import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import { canUseDataMapper } from "../rbac";
import {
  dataPlaneService,
  dataMapperSelection,
  dataMapperService,
  type DataMapperServicePath,
} from "../dataMapperServices";
import {
  isRecordChanged,
  sameId,
  visibleToSelection,
  type DataMapperLookupTable,
  type DataMapperMappingConfig,
  type DataMapperScope,
  type DataMapperSchemaEntry,
  type DataMapperSourceTable,
} from "../dataMapper.types";
import type { ParsedField, ParsedSchema, TransformConfig, TransformContext, TransformType } from "../engine/types";
import { defaultTransformConfig } from "../engine/transformCatalog";
import { previewTransformChain, type PreviewResult } from "../engine/transformPreview";
import { bindSelectionToSignedInUser, editorFailure } from "../shared/editorSupport";
import { asParsedSchema } from "../Schemas/DataMapperSchemasStore";
import type DataMapperProfileStateStore from "../DataMapperProfileStateStore";
import {
  draftKey,
  moveItem,
  sameMapping,
  toDraft,
  toPayload,
  type DraftMapping,
  type DraftTransform,
} from "./mappingDraft";
import {
  MAX_SUGGEST_FIELDS,
  MAX_SUGGEST_TABLES,
  suggestErrorMessage,
  suggestNotice,
  targetFieldPayload,
  type RegexExample,
  type RegexSuggestion,
  type SuggestMappingsAnswer,
} from "./llmSuggestions";
import { isSessionEnded, sessionMark } from "stores/sessionGuard";

export type MappingSaveOutcome = "saved" | "conflict" | "failed";

export const mappingRecordChangedNotice = (name: string): string =>
  `Someone else saved "${name}" after you opened it, so your changes were not saved. ` +
  "It has been reloaded with their version; make your changes again and save.";

/** At most this many pages of source tables are read (a page is under 1 MB). */
const SOURCE_TABLE_PAGES = 20;

/**
 * Data Mapper → Map and → Transform: ONE working copy of the open mapping
 * configuration, shared by both screens (the transforms live on the
 * mappings), so moving between them keeps unsaved edits.
 *
 * The configs themselves stay in the `data-mapper-mapping-configs`
 * ServiceStore (find = summaries, get = the whole config); the chosen config,
 * schema and mapping are ids on DataMapperViewStore. What lives here is the
 * FORM: the draft mappings and chains (mappingDraft.ts), the name, and the
 * `updatedAt` the draft was loaded from, sent back as `expectUpdatedAt`.
 *
 * Source columns come from the tab's connected source
 * (`data-mapper-source-tables`) when there is one; otherwise they are typed in.
 * Only table and column NAMES are kept here.
 */
class DataMapperMappingStore {
  rootStore: RootStore;

  loading = false;
  loaded = false;
  error: string | null = null;
  actionError: string | null = null;
  notice: string | null = null;

  // ---- The draft (form state) ----
  /** The profile the draft belongs to. */
  draftProfileId: string | null = null;
  /** The schema the draft maps onto (the selection's when it was started or loaded). */
  draftSchemaId: string | null = null;
  /** The config the draft was loaded from; null = a new, unsaved config. */
  loadedConfigId: string | null = null;
  editingUpdatedAt: string | null = null;
  configName = "";
  configDescription = "";
  /** Where a NEW config is saved (an existing one keeps its scope). */
  configScope: DataMapperScope = "profile";
  draft: DraftMapping[] = [];
  dirty = false;
  saving = false;
  formError: string | null = null;
  configLoading = false;

  // ---- Source columns (names only) ----
  sourceConnected = false;
  sourceChecked = false;
  sourceError: string | null = null;
  sourceTableNames: string[] = [];
  columnsByTable: Record<string, string[]> = {};
  /** The table new mappings default to. */
  currentSourceTable = "";

  // ---- Transform screen ----
  selectedMappingKey: string | null = null;
  previewInput = "";
  /** Other columns of the sample row, as JSON, for concat / template steps. */
  previewRowJson = "{}";
  lookupLoadError: string | null = null;
  /** Lookup tables whose entries are being fetched (ids). */
  lookupLoadingIds: string[] = [];

  // ---- AI suggestions (Map) ----
  suggesting = false;
  suggestError: string | null = null;
  suggestNotice: string | null = null;
  /**
   * Draft mappings the AI suggested that the person has not yet accepted or
   * rejected (keys). They are in the draft, unsaved, marked isAutoMapped with
   * their confidence; a save waits until none is left.
   */
  pendingSuggestionKeys: string[] = [];

  // ---- AI regex helper (Transform) ----
  /** The regex step a suggestion is being made for (its key), while the call runs. */
  regexSuggestingKey: string | null = null;
  regexSuggestion: (RegexSuggestion & { stepKey: string }) | null = null;
  regexError: { stepKey: string; message: string } | null = null;

  private loadSeq = 0;
  private openSeq = 0;
  private sourceSeq = 0;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // ---- Wiring --------------------------------------------------------------

  private service(path: DataMapperServicePath) {
    return dataMapperService(this.rootStore, path);
  }

  private get selection() {
    return dataMapperSelection(this.rootStore);
  }

  private get stateStore(): DataMapperProfileStateStore | null {
    return (
      (this.rootStore as unknown as { dataMapperProfileStateViewStore?: DataMapperProfileStateStore })
        .dataMapperProfileStateViewStore ?? null
    );
  }

  get canUse(): boolean {
    return canUseDataMapper(this.rootStore);
  }

  get profileId(): string | null {
    return this.selection?.selectedProfileId ?? null;
  }

  get clientId(): string | null {
    return this.selection?.selectedClientId ?? null;
  }

  // ---- Computed views over the ServiceStore caches -------------------------

  get schemaEntry(): DataMapperSchemaEntry | null {
    const id = this.selection?.selectedSchemaId;
    if (!id || !this.profileId) return null;
    const rows = (this.service("data-mapper-schemas")?.entities ?? []) as unknown as DataMapperSchemaEntry[];
    return rows.find((r) => sameId(r.id, id) && visibleToSelection(r, this.clientId, this.profileId)) ?? null;
  }

  get allConfigs(): DataMapperMappingConfig[] {
    if (!this.profileId) return [];
    const rows = (this.service("data-mapper-mapping-configs")?.entities ?? []) as unknown as DataMapperMappingConfig[];
    return rows.filter((r) => visibleToSelection(r, this.clientId, this.profileId));
  }

  get loadedConfig(): DataMapperMappingConfig | null {
    const id = this.loadedConfigId;
    return id ? (this.allConfigs.find((c) => sameId(c.id, id)) ?? null) : null;
  }

  /**
   * What the mappings map onto. The chosen schema when it is the config's
   * (by name), since it is the live definition; otherwise the config's own
   * saved copy; for a new config, the chosen schema.
   */
  get targetSchema(): ParsedSchema | null {
    const chosen = asParsedSchema(this.schemaEntry?.schema);
    const config = this.loadedConfig;
    if (config && this.loadedConfigId) {
      if (chosen && chosen.name === config.schemaName) return chosen;
      return asParsedSchema(config.schema) ?? chosen;
    }
    return chosen;
  }

  /** The configs for the target schema (by name, as the source matched them), narrowest scope first. */
  get configsForSchema(): DataMapperMappingConfig[] {
    const name = asParsedSchema(this.schemaEntry?.schema)?.name ?? this.targetSchema?.name;
    const rows = name ? this.allConfigs.filter((c) => c.schemaName === name) : this.allConfigs;
    return [...rows].sort((a, b) => a.name.localeCompare(b.name));
  }

  get targetFields(): ParsedField[] {
    return this.targetSchema?.fields ?? [];
  }

  /** Draft mappings for one target field. */
  mappingsFor(targetField: string): DraftMapping[] {
    return this.draft.filter((m) => m.targetField === targetField);
  }

  /** Draft mappings whose target is not a field of the target schema (renamed or removed). */
  get orphanMappings(): DraftMapping[] {
    const names = new Set(this.targetFields.map((f) => f.name));
    return this.draft.filter((m) => !names.has(m.targetField));
  }

  get unmappedRequiredCount(): number {
    return this.targetFields.filter((f) => !f.isOptional && this.mappingsFor(f.name).length === 0).length;
  }

  get lookupTables(): DataMapperLookupTable[] {
    if (!this.profileId) return [];
    const rows = (this.service("data-mapper-lookup-tables")?.entities ?? []) as unknown as DataMapperLookupTable[];
    return rows.filter((r) => visibleToSelection(r, this.clientId, this.profileId));
  }

  columnsFor(table: string): string[] {
    return this.columnsByTable[table] ?? [];
  }

  /** The column names offered for a table, the source's if known, plus every name the draft already uses. */
  columnOptions(table: string): string[] {
    const used = this.draft.filter((m) => m.sourceTable === table && m.sourceColumn).map((m) => m.sourceColumn);
    return [...new Set([...this.columnsFor(table), ...used])];
  }

  get tableOptions(): string[] {
    return [...new Set([...this.sourceTableNames, ...this.draft.map((m) => m.sourceTable).filter(Boolean)])];
  }

  // ---- Loading -------------------------------------------------------------

  /**
   * Route entry (Map or Transform). Every find names the selection. The chosen
   * config is (re)loaded into the draft unless the draft already holds it
   * with unsaved edits, which survive moving between the two screens.
   */
  async load(): Promise<void> {
    const seq = ++this.loadSeq;
    bindSelectionToSignedInUser(this.rootStore);
    const selection = this.selection;
    if (!this.canUse || !selection?.hasProfile) {
      this.loading = false;
      this.loaded = false;
      this.error = null;
      return;
    }
    const query = { ...selection.selectionQuery };
    if (!sameId(this.draftProfileId, query.profileId)) {
      // Another profile: nothing of the old one's draft or source applies.
      this.clearDraft(query.profileId ?? null);
      this.resetSource();
    } else if (!sameId(this.draftSchemaId, selection.selectedSchemaId)) {
      // Another schema was chosen: the draft was for the old one (Codex r1).
      this.clearDraft(query.profileId ?? null);
    }
    this.loading = true;
    this.error = null;
    const find = (path: DataMapperServicePath) => {
      const service = this.service(path);
      return service
        ? service.find({ query })
        : Promise.reject(new Error("The Data Mapper service is not available in this build."));
    };
    // The reset after a sign-out bumps loadSeq, but the epoch moves first: a
    // settle in between (one find failed on its own, the others were ended)
    // would still pass the seq check — and latch `loaded` over a reset store.
    const ended = sessionMark(this.rootStore);
    const results = await Promise.allSettled([
      find("data-mapper-schemas"),
      find("data-mapper-mapping-configs"),
      find("data-mapper-lookup-tables"),
    ]);
    const failure = results.find((r): r is PromiseRejectedResult => r.status === "rejected");
    if (seq !== this.loadSeq || ended()) return;
    if (failure) {
      runInAction(() => {
        this.loading = false;
        this.loaded = true;
        this.error = editorFailure(this.rootStore, failure.reason, query.profileId ?? null, "The mapping configurations could not be loaded.");
      });
      return;
    }
    const configId = selection.selectedMappingConfigId;
    if (configId && !this.allConfigs.some((c) => sameId(c.id, configId))) {
      // Deleted elsewhere, or not visible to this selection.
      selection.forgetMappingConfig(configId);
      if (sameId(this.loadedConfigId, configId)) this.clearDraft(query.profileId ?? null);
    } else if (configId && !(sameId(this.loadedConfigId, configId) && this.dirty)) {
      // Fresh from the API, unless the draft holds this config with unsaved edits.
      await this.openConfig(configId);
    } else if (!configId && this.loadedConfigId) {
      // The selection closed the config the draft holds.
      this.clearDraft(query.profileId ?? null);
    }
    if (ended()) return;
    runInAction(() => {
      if (seq !== this.loadSeq) return;
      this.loading = false;
      this.loaded = true;
      this.syncSelectedMapping();
    });
    void this.checkSource();
  }

  private clearDraft(profileId: string | null) {
    this.openSeq++; // a config still loading for the old draft must not land in this one
    this.configLoading = false;
    this.draftProfileId = profileId;
    this.draftSchemaId = this.selection?.selectedSchemaId ?? null;
    this.loadedConfigId = null;
    this.editingUpdatedAt = null;
    this.configName = "";
    this.configDescription = "";
    this.configScope = "profile";
    this.draft = [];
    this.dirty = false;
    this.formError = null;
    this.selectedMappingKey = null;
    this.clearSuggestions();
  }

  /**
   * Load a config into the draft (its whole body: `get`). The draft's unsaved
   * edits are dropped, so the screen asks first when `dirty`.
   */
  async openConfig(configId: string): Promise<boolean> {
    const service = this.service("data-mapper-mapping-configs");
    const query = { ...(this.selection?.selectionQuery ?? {}) };
    if (!service || this.saving) return false;
    // Latest request wins, and only for the profile + schema it was made under (Codex r1, r2).
    const seq = ++this.openSeq;
    const key = this.contextKey;
    const current = () => seq === this.openSeq && key === this.contextKey;
    this.configLoading = true;
    this.actionError = null;
    try {
      const config = (await service.get(configId, { query })) as unknown as DataMapperMappingConfig;
      if (!current()) return false;
      runInAction(() => {
        this.applyConfig(config);
        this.selection?.selectMappingConfig(config.id);
      });
      this.stateStore?.schedule();
      return true;
    } catch (err) {
      if (!current() || isSessionEnded(err)) return false;
      runInAction(() => {
        this.actionError = editorFailure(this.rootStore, err, query.profileId ?? null, "The mapping configuration could not be opened.");
        if ((err as { code?: unknown } | null)?.code === 404) this.selection?.forgetMappingConfig(configId);
      });
      return false;
    } finally {
      runInAction(() => {
        if (seq === this.openSeq) this.configLoading = false;
      });
    }
  }

  /** Replace the draft with a config the API returned. Keeps the open transform chain when it is still there. */
  private applyConfig(config: DataMapperMappingConfig) {
    const previous = this.draft.find((m) => m.key === this.selectedMappingKey) ?? null;
    this.draftProfileId = this.profileId;
    this.draftSchemaId = this.selection?.selectedSchemaId ?? null;
    this.loadedConfigId = config.id;
    this.editingUpdatedAt = config.updatedAt;
    this.configName = config.name;
    this.configDescription = config.description ?? "";
    this.configScope = config.scope;
    this.draft = toDraft(config.mappings);
    this.dirty = false;
    this.formError = null;
    this.clearSuggestions();
    const again = previous ? this.draft.find((m) => sameMapping(m, previous)) : undefined;
    this.selectedMappingKey = again?.key ?? null;
    if (again) this.selection?.selectMapping(again.id);
    else this.syncSelectedMapping();
  }

  /** Point the Transform screen at the mapping the selection names (a saved id). */
  private syncSelectedMapping() {
    const wanted = this.selection?.selectedMappingId;
    if (this.selectedMappingKey && this.draft.some((m) => m.key === this.selectedMappingKey)) return;
    const found = wanted ? this.draft.find((m) => sameId(m.id, wanted)) : undefined;
    this.selectedMappingKey = found?.key ?? null;
    this.loadLookupsForSelected();
  }

  /** Start a new, unsaved config for the chosen schema. */
  startNewConfig() {
    if (this.saving) return;
    this.clearDraft(this.profileId);
    this.configName = this.targetSchema ? `${this.targetSchema.name} mapping` : "";
    this.selection?.selectMappingConfig(null);
    this.stateStore?.schedule();
  }

  // ---- Source columns ------------------------------------------------------

  /** Which profile + schema the draft is for: an answer for another context is never applied. */
  private get contextKey(): string {
    return `${String(this.selection?.selectedProfileId ?? "").toLowerCase()}|${String(this.selection?.selectedSchemaId ?? "").toLowerCase()}`;
  }

  /**
   * The schema choice changed (Schema screen): close the draft made for the old
   * schema now, not on the next Map visit, and drop any config still loading
   * for it. The screen confirms first when the draft is dirty.
   */
  schemaChanged() {
    if (!sameId(this.draftSchemaId, this.selection?.selectedSchemaId)) this.clearDraft(this.profileId);
  }

  /** The ONE tab id and selection every data-plane call names (the Source screen's, so both see the same open source). */
  private get dataPlaneQuery(): Record<string, unknown> {
    return { ...(this.selection?.dataPlaneQuery ?? {}) };
  }

  /** Is a source open in this tab for this selection? If so, list its tables. Failures mean "type them in". */
  async checkSource(): Promise<void> {
    const dataSource = dataPlaneService(this.rootStore, "data-mapper-data-source");
    const tables = dataPlaneService(this.rootStore, "data-mapper-source-tables");
    if (!dataSource || !tables || !this.selection?.hasProfile) return;
    // Answers for an older check, or another profile, are dropped (Codex r1).
    const seq = ++this.sourceSeq;
    const query = this.dataPlaneQuery;
    try {
      const status = (await dataSource.create({ action: "status", ...query })) as { connected?: boolean };
      if (seq !== this.sourceSeq) return;
      if (!status?.connected) {
        runInAction(() => {
          this.sourceConnected = false;
          this.sourceChecked = true;
          this.sourceError = null;
          this.sourceTableNames = [];
        });
        return;
      }
      const names: string[] = [];
      let skip: number | null = 0;
      for (let page = 0; page < SOURCE_TABLE_PAGES && skip !== null; page++) {
        const answer = (await tables.find({ query: { ...query, $skip: skip } })) as {
          rows?: DataMapperSourceTable[];
          nextCursor?: number | null;
        };
        for (const t of answer?.rows ?? []) names.push(t.fullName ?? t.name);
        skip = typeof answer?.nextCursor === "number" ? answer.nextCursor : null;
      }
      if (seq !== this.sourceSeq) return;
      runInAction(() => {
        this.sourceConnected = true;
        this.sourceChecked = true;
        this.sourceError = null;
        this.sourceTableNames = names;
        if (!this.currentSourceTable && names.length) this.currentSourceTable = names[0];
      });
      if (this.currentSourceTable) void this.loadColumns(this.currentSourceTable);
    } catch (err) {
      if (seq !== this.sourceSeq || isSessionEnded(err)) return;
      runInAction(() => {
        this.sourceConnected = false;
        this.sourceChecked = true;
        this.sourceError = (err as { message?: string } | null)?.message ?? "The source could not be read.";
      });
    }
  }

  /** Forget the previous profile's source: its tables, columns and default table. */
  private resetSource() {
    this.sourceSeq++;
    this.sourceConnected = false;
    this.sourceChecked = false;
    this.sourceError = null;
    this.sourceTableNames = [];
    this.columnsByTable = {};
    this.currentSourceTable = "";
  }

  /** One table's column names, from the connected source. */
  async loadColumns(table: string): Promise<void> {
    if (!this.sourceConnected || !table || this.columnsByTable[table]) return;
    const tables = dataPlaneService(this.rootStore, "data-mapper-source-tables");
    if (!tables) return;
    const seq = this.sourceSeq;
    try {
      const detail = (await tables.get(table, { query: this.dataPlaneQuery })) as DataMapperSourceTable;
      if (seq !== this.sourceSeq) return;
      runInAction(() => {
        this.columnsByTable = { ...this.columnsByTable, [table]: (detail?.columns ?? []).map((c) => c.name) };
      });
    } catch (err) {
      if (seq !== this.sourceSeq) return;
      runInAction(() => {
        if (!isSessionEnded(err)) this.sourceError = (err as { message?: string } | null)?.message ?? `The columns of ${table} could not be read.`;
      });
    }
  }

  setCurrentSourceTable(table: string) {
    this.currentSourceTable = table;
    void this.loadColumns(table);
  }

  // ---- Editing the mappings (Map) -----------------------------------------

  private touch() {
    this.dirty = true;
    this.formError = null;
  }

  setConfigName(v: string) {
    if (this.saving) return; // the draft is frozen while a save is in flight (Codex r1)
    this.configName = v;
    this.touch();
  }

  setConfigDescription(v: string) {
    if (this.saving) return; // the draft is frozen while a save is in flight (Codex r1)
    this.configDescription = v;
    this.touch();
  }

  setConfigScope(v: DataMapperScope) {
    if (this.saving) return; // the draft is frozen while a save is in flight (Codex r1)
    this.configScope = v;
  }

  /** Map a target field: a new mapping from the current source table. */
  addMapping(targetField: string, sourceColumn = ""): DraftMapping | null {
    if (this.saving) return null;
    const mapping: DraftMapping = {
      key: draftKey("m"),
      id: null,
      sourceTable: this.currentSourceTable,
      sourceColumn,
      targetField,
      isAutoMapped: false,
      confidence: null,
      notes: null,
      transforms: [],
    };
    this.draft = [...this.draft, mapping];
    this.touch();
    return mapping;
  }

  /**
   * Change where a mapping reads from. A person choosing the column makes it
   * a manual mapping: the auto-mapped flag and its confidence no longer apply.
   */
  setMappingSource(key: string, patch: { sourceTable?: string; sourceColumn?: string }) {
    if (this.saving) return; // the draft is frozen while a save is in flight (Codex r1)
    const current = this.draft.find((m) => m.key === key);
    if (
      !current ||
      ((patch.sourceTable === undefined || patch.sourceTable === current.sourceTable) &&
        (patch.sourceColumn === undefined || patch.sourceColumn === current.sourceColumn))
    ) {
      return; // nothing changed (an input echoing its own value)
    }
    this.draft = this.draft.map((m) =>
      m.key === key ? { ...m, ...patch, isAutoMapped: false, confidence: null } : m
    );
    // Choosing the column yourself decides a pending suggestion: it is now a manual mapping.
    this.forgetPending(key);
    if (patch.sourceTable) void this.loadColumns(patch.sourceTable);
    this.touch();
  }

  removeMapping(key: string) {
    if (this.saving) return; // the draft is frozen while a save is in flight (Codex r1)
    this.draft = this.draft.filter((m) => m.key !== key);
    this.forgetPending(key);
    if (this.selectedMappingKey === key) this.selectMappingForTransforms(null);
    this.touch();
  }

  // ---- AI mapping suggestions (Map) ----------------------------------------

  private clearSuggestions() {
    this.pendingSuggestionKeys = [];
    this.suggestNotice = null;
    this.suggestError = null;
    this.regexSuggestion = null;
    this.regexError = null;
  }

  private forgetPending(key: string) {
    if (this.pendingSuggestionKeys.includes(key)) {
      this.pendingSuggestionKeys = this.pendingSuggestionKeys.filter((k) => k !== key);
    }
  }

  isPendingSuggestion(key: string): boolean {
    return this.pendingSuggestionKeys.includes(key);
  }

  /** The target fields a suggestion is asked for: those the draft does not map yet. */
  get suggestTargetFields(): ParsedField[] {
    const mapped = new Set(this.draft.map((m) => m.targetField));
    return this.targetFields.filter((f) => !mapped.has(f.name)).slice(0, MAX_SUGGEST_FIELDS);
  }

  /**
   * The source tables a suggestion reads: the table for new mappings, then the
   * tables the draft already maps from, as far as the open source has them.
   */
  get suggestTables(): string[] {
    const known = new Set(this.sourceTableNames);
    return [...new Set([this.currentSourceTable, ...this.draft.map((m) => m.sourceTable)])]
      .filter((t) => t && known.has(t))
      .slice(0, MAX_SUGGEST_TABLES);
  }

  /** Why "Suggest mappings" cannot run now, or null. */
  get suggestProblem(): string | null {
    if (this.saving) return "Wait for the save to finish.";
    if (!this.sourceConnected) return "Connect a source in this tab (the Source step) first: suggestions read its columns.";
    if (this.suggestTables.length === 0) return "Choose the source table for new mappings first.";
    if (this.suggestTargetFields.length === 0) return "Every target field is already mapped.";
    return null;
  }

  /**
   * Ask the AI which source columns fill the unmapped target fields. The
   * suggestions arrive as unsaved draft mappings, marked isAutoMapped with
   * their confidence, and pending until the person accepts or rejects each.
   */
  async suggestMappings(): Promise<void> {
    const service = dataPlaneService(this.rootStore, "data-mapper-llm-mapping");
    const problem = this.suggestProblem;
    if (!service || problem || this.suggesting) {
      if (problem) this.suggestError = problem;
      return;
    }
    const key = this.contextKey;
    const openSeq = this.openSeq;
    const current = () => key === this.contextKey && openSeq === this.openSeq;
    this.suggesting = true;
    this.suggestError = null;
    this.suggestNotice = null;
    try {
      const answer = (await service.create({
        action: "suggest-mappings",
        targetFields: this.suggestTargetFields.map(targetFieldPayload),
        tables: this.suggestTables,
        ...this.dataPlaneQuery,
      })) as SuggestMappingsAnswer;
      if (!current()) return;
      runInAction(() => {
        // A field mapped while the call ran keeps the person's mapping.
        const mapped = new Set(this.draft.map((m) => m.targetField));
        const added: DraftMapping[] = (answer?.suggestions ?? [])
          .filter((s) => !mapped.has(s.targetField))
          .map((s) => ({
            key: draftKey("m"),
            id: null,
            sourceTable: s.sourceTable,
            sourceColumn: s.sourceColumn,
            targetField: s.targetField,
            isAutoMapped: true,
            confidence: s.confidence,
            notes: s.reasoning ? `AI: ${s.reasoning}` : null,
            transforms: [],
          }));
        if (added.length > 0) {
          this.draft = [...this.draft, ...added];
          this.pendingSuggestionKeys = [...this.pendingSuggestionKeys, ...added.map((m) => m.key)];
          this.touch();
          for (const t of new Set(added.map((m) => m.sourceTable))) void this.loadColumns(t);
        }
        this.suggestNotice = suggestNotice(answer ?? { suggestions: [], dropped: 0 }, added.length);
      });
    } catch (err) {
      if (!current()) return;
      runInAction(() => {
        if (!isSessionEnded(err)) this.suggestError = suggestErrorMessage(err, "The AI suggestions could not be made.");
      });
    } finally {
      runInAction(() => {
        this.suggesting = false;
      });
    }
  }

  /** Keep a suggested mapping (it stays in the draft, still marked as auto-mapped). */
  acceptSuggestion(key: string) {
    if (this.saving) return;
    this.forgetPending(key);
  }

  /** Drop a suggested mapping from the draft. */
  rejectSuggestion(key: string) {
    if (this.saving || !this.isPendingSuggestion(key)) return;
    this.removeMapping(key);
  }

  acceptAllSuggestions() {
    if (this.saving) return;
    this.pendingSuggestionKeys = [];
  }

  rejectAllSuggestions() {
    if (this.saving) return;
    for (const key of [...this.pendingSuggestionKeys]) this.removeMapping(key);
  }

  clearSuggestNotice() {
    this.suggestNotice = null;
    this.suggestError = null;
  }

  // ---- Editing a chain (Transform) ----------------------------------------

  get selectedMapping(): DraftMapping | null {
    return this.draft.find((m) => m.key === this.selectedMappingKey) ?? null;
  }

  selectMappingForTransforms(key: string | null) {
    // The sample value belongs to the chain it was typed for: carried to another field it ran
    // that field's steps on the wrong column's value and showed their failure (e.g. a Kind
    // code "W" under "Sample WeightKg", reported as `Cannot convert "W" to number`).
    if (key !== this.selectedMappingKey) this.previewInput = "";
    this.selectedMappingKey = key;
    const mapping = key ? this.draft.find((m) => m.key === key) : null;
    this.selection?.selectMapping(mapping?.id ?? null);
    this.stateStore?.schedule();
    this.loadLookupsForSelected();
  }

  /** The lookup tables the open chain's steps name, loaded for the preview. */
  private loadLookupsForSelected() {
    for (const t of this.selectedMapping?.transforms ?? []) {
      const id = (t.config as { lookupTableId?: unknown }).lookupTableId;
      if (t.type === "valueLookup" && typeof id === "string" && id) void this.loadLookupEntries(id);
    }
  }

  /** A lookup table the open chain needs that is still loading: the preview waits for it. */
  get previewWaitsForLookup(): boolean {
    const loaded = this.lookupContext.lookupTables ?? {};
    return (this.selectedMapping?.transforms ?? []).some((t) => {
      const id = (t.config as { lookupTableId?: unknown }).lookupTableId;
      return t.enabled && t.type === "valueLookup" && typeof id === "string" && !!id && !loaded[id] && this.lookupLoadingIds.includes(id);
    });
  }

  private updateChain(mappingKey: string, change: (chain: DraftTransform[]) => DraftTransform[]) {
    if (this.saving) return; // the draft is frozen while a save is in flight (Codex r1)
    this.draft = this.draft.map((m) => (m.key === mappingKey ? { ...m, transforms: change(m.transforms) } : m));
    this.touch();
  }

  addTransform(mappingKey: string, type: TransformType): void {
    this.updateChain(mappingKey, (chain) => [
      ...chain,
      { key: draftKey("t"), id: null, type, config: defaultTransformConfig(type), enabled: true },
    ]);
  }

  removeTransform(mappingKey: string, transformKey: string): void {
    this.updateChain(mappingKey, (chain) => chain.filter((t) => t.key !== transformKey));
  }

  /** Move a step from one position to another; `order` follows the position on save. */
  moveTransform(mappingKey: string, from: number, to: number): void {
    this.updateChain(mappingKey, (chain) => moveItem(chain, from, to));
  }

  setTransformEnabled(mappingKey: string, transformKey: string, enabled: boolean): void {
    this.updateChain(mappingKey, (chain) => chain.map((t) => (t.key === transformKey ? { ...t, enabled } : t)));
  }

  updateTransformConfig(mappingKey: string, transformKey: string, patch: Partial<TransformConfig>): void {
    this.updateChain(mappingKey, (chain) =>
      chain.map((t) => (t.key === transformKey ? { ...t, config: { ...t.config, ...patch } as TransformConfig } : t))
    );
    const lookupTableId = (patch as { lookupTableId?: string }).lookupTableId;
    if (lookupTableId) void this.loadLookupEntries(lookupTableId);
  }

  // ---- AI regex helper (Transform) -----------------------------------------

  /**
   * Ask the AI for a regex that turns each example input into its output. The
   * API tries it in its sandbox first and says how many examples it satisfies;
   * nothing changes in the step until the person applies it.
   */
  async suggestRegex(stepKey: string, examples: RegexExample[], description = ""): Promise<void> {
    const service = dataPlaneService(this.rootStore, "data-mapper-llm-mapping");
    if (!service || this.regexSuggestingKey || examples.length === 0) return;
    // An answer for a draft since replaced (another config opened or reloaded) is dropped (Codex r1).
    const key = this.contextKey;
    const openSeq = this.openSeq;
    const current = () => key === this.contextKey && openSeq === this.openSeq;
    this.regexSuggestingKey = stepKey;
    this.regexError = null;
    this.regexSuggestion = null;
    try {
      const answer = (await service.create({
        action: "suggest-regex",
        examples,
        ...(description.trim() ? { description: description.trim().slice(0, 500) } : {}),
        ...this.dataPlaneQuery,
      })) as RegexSuggestion;
      if (!current()) return;
      runInAction(() => {
        this.regexSuggestion = { ...answer, stepKey };
      });
    } catch (err) {
      if (!current()) return;
      runInAction(() => {
        if (!isSessionEnded(err)) this.regexError = { stepKey, message: suggestErrorMessage(err, "The regex could not be suggested.") };
      });
    } finally {
      runInAction(() => {
        this.regexSuggestingKey = null;
      });
    }
  }

  /** Put the suggested regex into its step (the draft; Save persists it). */
  applyRegexSuggestion(mappingKey: string): void {
    const s = this.regexSuggestion;
    if (!s || this.saving) return;
    this.updateTransformConfig(mappingKey, s.stepKey, {
      pattern: s.pattern,
      replacement: s.replacement,
      flags: s.flags,
    } as Partial<TransformConfig>);
    this.regexSuggestion = null;
  }

  dismissRegexSuggestion(): void {
    this.regexSuggestion = null;
    this.regexError = null;
  }

  // ---- Preview (client-side, built-ins only) -------------------------------

  setPreviewInput(v: string) {
    this.previewInput = v;
  }

  setPreviewRowJson(v: string) {
    this.previewRowJson = v;
  }

  get previewRow(): { row: Record<string, unknown>; error: string | null } {
    const text = this.previewRowJson.trim();
    if (!text) return { row: {}, error: null };
    try {
      const parsed = JSON.parse(text) as unknown;
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return { row: parsed as Record<string, unknown>, error: null };
      }
      return { row: {}, error: "The other columns must be a JSON object, e.g. {\"LastName\": \"Smith\"}." };
    } catch {
      return { row: {}, error: "The other columns are not valid JSON." };
    }
  }

  /** Lookup tables whose entries have been loaded, for the preview. */
  get lookupContext(): TransformContext {
    const tables: Record<string, Array<{ sourceValue: string; targetValue: unknown }>> = {};
    for (const t of this.lookupTables) if (Array.isArray(t.entries)) tables[t.id] = t.entries;
    return { lookupTables: tables };
  }

  /**
   * The selected mapping's chain on the sample value: built-in steps run here;
   * a custom step is never run in the browser and the preview stops there.
   */
  get preview(): PreviewResult | null {
    const mapping = this.selectedMapping;
    if (!mapping) return null;
    const { row } = this.previewRow;
    const steps = mapping.transforms.map((t, order) => ({ id: t.key, type: t.type, config: t.config, order, enabled: t.enabled }));
    return previewTransformChain(this.previewInput, steps, { ...row, [mapping.sourceColumn]: this.previewInput }, this.lookupContext);
  }

  /** Load a lookup table's entries (its `get`) so the preview can use it. */
  async loadLookupEntries(lookupTableId: string): Promise<void> {
    const service = this.service("data-mapper-lookup-tables");
    if (!service) return;
    const cached = this.lookupTables.find((t) => sameId(t.id, lookupTableId));
    if ((cached && Array.isArray(cached.entries)) || this.lookupLoadingIds.includes(lookupTableId)) return;
    this.lookupLoadingIds = [...this.lookupLoadingIds, lookupTableId];
    try {
      await service.get(lookupTableId, { query: { ...(this.selection?.selectionQuery ?? {}) } });
      runInAction(() => {
        this.lookupLoadError = null;
      });
    } catch (err) {
      runInAction(() => {
        if (!isSessionEnded(err)) this.lookupLoadError = (err as { message?: string } | null)?.message ?? "The lookup table could not be loaded.";
      });
    } finally {
      runInAction(() => {
        this.lookupLoadingIds = this.lookupLoadingIds.filter((id) => id !== lookupTableId);
      });
    }
  }

  // ---- Saving --------------------------------------------------------------

  get saveProblem(): string | null {
    if (!this.selection?.hasProfile) return "Open a profile first.";
    if (!this.targetSchema) return "Choose a target schema on the Schema step first.";
    if (!this.configName.trim()) return "The mapping configuration needs a name.";
    // A save while suggestions are on their way would race them (Codex r1).
    if (this.suggesting) return "Wait for the AI suggestions to arrive.";
    const pending = this.pendingSuggestionKeys.length;
    if (pending > 0) {
      return `Accept or reject the ${pending} suggested mapping${pending === 1 ? "" : "s"} first.`;
    }
    const blank = this.draft.find((m) => !m.sourceColumn.trim());
    if (blank) return `Choose a source column for "${blank.targetField}", or remove that mapping.`;
    return null;
  }

  /**
   * Save the draft. An existing config is patched with the `updatedAt` it was
   * loaded from and only its own child ids; a new one is created with none.
   * On 409 `record-changed` the config is reloaded with the other person's
   * version (the draft's edits are dropped, and the page says so).
   */
  async save(): Promise<MappingSaveOutcome> {
    const problem = this.saveProblem;
    if (problem) {
      this.formError = problem;
      return "failed";
    }
    const service = this.service("data-mapper-mapping-configs");
    if (!service) {
      this.formError = "The Data Mapper service is not available in this build.";
      return "failed";
    }
    const query = { ...(this.selection?.selectionQuery ?? {}) };
    const schema = this.targetSchema as ParsedSchema;
    const name = this.configName.trim();
    const configId = this.loadedConfigId;
    const body = {
      name,
      description: this.configDescription.trim() || null,
      schemaName: schema.name,
      schema,
    };
    const key = this.contextKey;
    this.saving = true;
    this.formError = null;
    this.notice = null;
    try {
      let saved: DataMapperMappingConfig;
      if (configId) {
        const patch: Record<string, unknown> = { ...body, mappings: toPayload(this.draft, { keepIds: true }) };
        if (this.editingUpdatedAt) patch.expectUpdatedAt = this.editingUpdatedAt;
        saved = (await service.patch(configId, patch as never, { query })) as unknown as DataMapperMappingConfig;
      } else {
        saved = (await service.create({
          ...body,
          mappings: toPayload(this.draft, { keepIds: false }),
          scope: this.configScope,
          ...query,
        } as never)) as unknown as DataMapperMappingConfig;
      }
      // Saved. The answer belongs to the profile + schema it was saved under; the tab may have
      // moved on during the request, and then it must not land in the new context (Codex r2).
      if (key !== this.contextKey) return "saved";
      runInAction(() => {
        this.applyConfig(saved);
        this.selection?.selectMappingConfig(saved.id);
      });
      this.stateStore?.schedule();
      return "saved";
    } catch (err) {
      if (key !== this.contextKey || isSessionEnded(err)) return "failed";
      if (configId && isRecordChanged(err)) {
        runInAction(() => {
          this.notice = mappingRecordChangedNotice(name);
          this.saving = false; // the reload below is not a second save
        });
        await this.openConfig(configId);
        return "conflict";
      }
      runInAction(() => {
        this.formError = editorFailure(this.rootStore, err, query.profileId ?? null, "The mapping configuration could not be saved.");
      });
      return "failed";
    } finally {
      runInAction(() => {
        this.saving = false;
      });
    }
  }

  /** Delete the loaded config (the caller confirms first). */
  async removeLoadedConfig(): Promise<boolean> {
    const configId = this.loadedConfigId;
    const service = this.service("data-mapper-mapping-configs");
    if (!configId || !service || this.saving) return false;
    const query = { ...(this.selection?.selectionQuery ?? {}) };
    this.actionError = null;
    try {
      await service.remove(configId, { query });
      runInAction(() => {
        this.selection?.forgetMappingConfig(configId);
        this.clearDraft(this.profileId);
      });
      this.stateStore?.schedule();
      return true;
    } catch (err) {
      runInAction(() => {
        if (!isSessionEnded(err)) this.actionError = editorFailure(this.rootStore, err, query.profileId ?? null, "The mapping configuration could not be deleted.");
      });
      return false;
    }
  }

  clearNotice() {
    this.notice = null;
  }

  clearActionError() {
    this.actionError = null;
  }

  /**
   * Route exit: drop the messages, keep the draft (moving between Map and
   * Transform must not lose unsaved edits; a different profile clears it on
   * the next load).
   */
  reset() {
    this.loadSeq++;
    this.openSeq++; // a config still loading is re-requested by the next load
    this.configLoading = false;
    this.loading = false;
    this.error = null;
    this.actionError = null;
    this.formError = null;
    this.notice = null;
  }

  /**
   * Everything reset() keeps across this wizard's route changes goes too: on
   * sign-out it was the previous person's. Called by RootStore.purgeUserData,
   * which prefers this to reset().
   */
  resetForSignOut() {
    this.reset();
    this.loaded = false;
    this.draftProfileId = null;
    this.draftSchemaId = null;
    this.loadedConfigId = null;
    this.editingUpdatedAt = null;
    this.configName = "";
    this.configDescription = "";
    this.configScope = "profile";
    this.draft = [];
    this.dirty = false;
    this.saving = false;
    // Through resetSource, which also moves `sourceSeq`: a source check or a
    // column read still out for the person who left must find its answer
    // stale, not write `sourceChecked`/`columnsByTable` onto this reset store.
    this.resetSource();
    this.selectedMappingKey = null;
    this.previewInput = "";
    this.previewRowJson = "{}";
    this.lookupLoadError = null;
    this.lookupLoadingIds = [];
    this.suggesting = false;
    this.suggestError = null;
    this.suggestNotice = null;
    this.pendingSuggestionKeys = [];
    this.regexSuggestingKey = null;
    this.regexSuggestion = null;
    this.regexError = null;
  }
}

export default DataMapperMappingStore;
